import { useCallback } from 'react';

import { createHook, createStore } from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer';

export const PROMPT_PLACEHOLDER_CHAR = '`';

type PlaceholderType = 'person' | 'link' | 'generic';

type PlaceholderData = {
	prompt: string;
	adfDocument?: DocNode;
	type: PlaceholderType;
	selectionStartIndex: number;
	selectionEndIndex: number;
};

type State = {
	placeholder: PlaceholderData | null;
};

const state = createStore({
	initialState: {
		placeholder: null,
	} as State,
	actions: {
		setPlaceholder:
			(placeholder: State['placeholder']) =>
			({ setState }) => {
				setState({ placeholder });
			},
	},
});

// exported for testing
export const usePlaceholderStore = createHook(state);

const getPlaceholderType = (placeholderLabel: string): PlaceholderType => {
	switch (placeholderLabel) {
		case 'person':
			return 'person';
		default:
			return 'link';
	}
};

const isPromptWithPlaceholder = (prompt: string) => {
	return prompt.includes(PROMPT_PLACEHOLDER_CHAR);
};

export const useEditorPlaceholder = () => {
	const [{ placeholder }, { setPlaceholder }] = usePlaceholderStore();

	const insertPlaceholder = useCallback(
		(prompt: string, overrideType?: PlaceholderType) => {
			const startIndexOfPlaceholder = prompt.indexOf(PROMPT_PLACEHOLDER_CHAR);
			const endIndexOfPlaceholder = prompt.lastIndexOf(PROMPT_PLACEHOLDER_CHAR);
			if (startIndexOfPlaceholder !== -1 && endIndexOfPlaceholder !== -1) {
				const placeholderLabel = prompt.slice(startIndexOfPlaceholder + 1, endIndexOfPlaceholder);
				const placeholderType = overrideType ?? getPlaceholderType(placeholderLabel);
				const selectionStartIndex = startIndexOfPlaceholder + 1;
				const selectionEndIndex = endIndexOfPlaceholder;

				const promptWithoutBackticks = prompt.replace(new RegExp(PROMPT_PLACEHOLDER_CHAR, 'g'), '');
				const adfDocument = encodeAdf(promptWithoutBackticks);

				setPlaceholder({
					prompt: promptWithoutBackticks,
					adfDocument,
					type: placeholderType,
					selectionStartIndex,
					selectionEndIndex,
				});
			} else {
				// A proper placeholder was not found
				setPlaceholder({
					prompt: prompt,
					type: 'generic',
					selectionStartIndex: prompt.length + 1,
					selectionEndIndex: prompt.length + 1,
				});
			}
		},
		[setPlaceholder],
	);

	const clearPlaceholder = useCallback(() => {
		setPlaceholder(null);
	}, [setPlaceholder]);

	return {
		insertPlaceholder,
		clearPlaceholder,
		placeholder,
		isPromptWithPlaceholder,
	};
};
